import { useId, useState } from 'react';
import { IconCheck } from '@/components/Icons';
import { LeadPicker, type PickedLead } from '@/components/leads/LeadPicker';
import { formatBRL } from '@/lib/format';
import type { Receipt } from '@/lib/types';

type DepositKind = 'WELCOME' | 'EVENT';

const KINDS: { value: DepositKind; label: string }[] = [
  { value: 'WELCOME', label: 'Boas-vindas' },
  { value: 'EVENT', label: 'Evento' },
];

/** As descrições prontas de antes: trocam junto com o tipo; uma escrita à mão fica. */
const AUTO_DESCRIPTIONS = ['', 'Depósito'];

interface ClassifyDepositPanelProps {
  receipt: Receipt;
  isAdmin: boolean;
  saving: boolean;
  onSave: (body: Record<string, unknown>, message: string) => Promise<boolean>;
}

/**
 * O depósito sem tipo fica pendente até alguém dizer se foi de boas-vindas ou
 * de evento. Aqui ele vira o depósito com alavancagem: o valor depositado é o
 * que já estava (falta só o cliente, se não tiver), e a alavancagem começa
 * pendente — a buscada entra quando ela for realizada.
 */
export function ClassifyDepositPanel({ receipt, isAdmin, saving, onSave }: ClassifyDepositPanelProps) {
  const id = useId();
  const [kind, setKind] = useState<DepositKind | null>(null);
  const [lead, setLead] = useState<PickedLead | null>(null);
  const [error, setError] = useState<string | null>(null);
  const needsLead = !receipt.leadId;

  async function submit() {
    if (!kind) return setError('Escolha: boas-vindas ou evento.');
    if (needsLead && !lead) return setError('Escolha o cliente do depósito.');
    setError(null);
    const description = AUTO_DESCRIPTIONS.includes(receipt.description.trim())
      ? { description: kind === 'WELCOME' ? 'Alavancagem boas-vindas' : 'Alavancagem evento' }
      : {};
    await onSave(
      {
        leverageType: kind,
        // O que estava como valor do depósito é o depositado; a buscada entra com a alavancagem realizada.
        depositAmount: receipt.soldAmount,
        soldAmount: 0,
        ...description,
        ...(needsLead && lead ? { leadId: lead.id } : {}),
      },
      `Depósito marcado como ${kind === 'WELCOME' ? 'boas-vindas' : 'evento'}.`,
    );
  }

  return (
    <section className="classify-deposit" aria-labelledby={`${id}-title`}>
      <div>
        <div id={`${id}-title`} className="classify-deposit-title">Tipo pendente</div>
        <p className="classify-deposit-lede muted">
          Este depósito de <strong className="num">{formatBRL(receipt.soldAmount)}</strong> foi de boas-vindas ou de evento?
          Com o tipo, ele vira uma alavancagem pendente; a buscada entra quando ela for realizada.
        </p>
      </div>

      <fieldset className="field" style={{ margin: 0, padding: 0, border: 0 }}>
        <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>Tipo do depósito *</legend>
        <div className="choice-group">
          {KINDS.map((option) => (
            <label key={option.value} className="choice">
              <input
                type="radio"
                name={`${id}-kind`}
                value={option.value}
                checked={kind === option.value}
                onChange={() => setKind(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {needsLead ? (
        <LeadPicker
          value={lead?.id ?? ''}
          valueName={lead?.name ?? ''}
          isAdmin={isAdmin}
          required
          fromBase
          allowCreate
          {...(isAdmin ? { newLeadOwnerId: receipt.memberId } : {})}
          onChange={setLead}
        />
      ) : null}

      {error ? <div className="alert alert-error" role="alert">{error}</div> : null}

      <div>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
          {saving ? <span className="spinner" /> : <IconCheck />} Salvar o tipo
        </button>
      </div>
    </section>
  );
}
